import type { OrderCancellationCode } from "@loot-os/core/db/schema";
import type { DbClient } from "../shared/db";

/** Every order type, in one list so validators derive from the type itself. */
export const ORDER_TYPES = [
  "TCG",
  "MARKETPLACE",
  "WITHDRAW_ASSET",
  "WITHDRAW_STEAM",
  "SELL_FOR_CRYPTO",
  "EXTERNAL",
] as const;

export type OrderType = (typeof ORDER_TYPES)[number];

export type FulfillmentMethod =
  | "FACILITY_SHIP"
  | "PARTNER_DROPSHIP"
  | "MANUAL_BUY"
  | "BUY_BOT"
  | "CRYPTO_TX"
  | "STEAM_TRADE";

/** Every fulfillment method, in one list so validators derive from the type itself. */
export const FULFILLMENT_METHODS = [
  "FACILITY_SHIP",
  "PARTNER_DROPSHIP",
  "MANUAL_BUY",
  "BUY_BOT",
  "CRYPTO_TX",
  "STEAM_TRADE",
] as const satisfies readonly FulfillmentMethod[];

/** Every order status, in one list so validators derive from the type itself. */
export const ORDER_STATUSES = [
  "PENDING",
  "PROCESSING",
  "PRESHIPPED",
  "SHIPPED",
  "CANCELLED",
  "UNAPPROVED",
  "APPROVED",
  "HOLD",
  "SUCCESS",
  "FAIL",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_LINE_STATUSES = ["PENDING", "PACKED", "CANCELLED"] as const;

export type OrderLineStatus = (typeof ORDER_LINE_STATUSES)[number];

export type ItemSource = "OWNED" | "MARKETPLACE" | "CATALOG";

export interface CreateOrderLineInput {
  itemSource: OrderLine["itemSource"];
  itemId: string;
  unitValue: number;
  inventoryItemId?: string;
  snapshot: Record<string, unknown>;
  fulfillmentFee?: number;
  isSlowShip?: boolean;
}

export interface Order {
  id: string;
  endUserId: string;
  tenantId: string;
  type: OrderType;
  vendorId: string | null;
  method: FulfillmentMethod | null;
  status: OrderStatus;
  paidWith: "ITEMS" | "BALANCE" | null;
  shippingAddress: Record<string, unknown> | null;
  shippingFee: number;
  shippingFeeRefunded: boolean;
  totalCost: number | null;
  trackingNumber: string | null;
  tenantReferenceId: string | null;
  details: Record<string, unknown> | null;
  marketplaceTxId: string | null;
  consumablesUsed: string[];
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
  processingAt: Date | null;
  preshippedAt: Date | null;
  shippedAt: Date | null;
  cancelledAt: Date | null;
  cancellationReason: string | null;
  /** Why it was cancelled. Null on a cancel nobody coded, including every row migrated from boxed. */
  cancellationCode: OrderCancellationCode | null;
}

/** Longest cancel note either cancel path accepts, and what the text column is sized for. */
export const MAX_CANCELLATION_REASON = 1000;

/** Why a cancel happened: the coded cause a tenant branches on, plus the operator's own words. */
export interface OrderCancellation {
  code?: OrderCancellationCode;
  reason?: string;
}

/**
 * Work a caller adds to a whole-order cancel. `onOrderCancelled` runs inside the cancel's
 * transaction, after the cancel writes, with the cancelled order. When it throws, the cancel rolls
 * back and a retry can run both again. It never runs for a partial line cancel.
 */
export interface OrderCancelHooks {
  onOrderCancelled?: (tx: DbClient, order: Order) => Promise<void>;
}

export interface OrderLine {
  id: string;
  orderId: string;
  itemSource: ItemSource;
  itemId: string;
  inventoryItemId: string | null;
  snapshot: Record<string, unknown>;
  unitValue: number;
  fulfillmentFee: number | null;
  isSlowShip: boolean;
  status: OrderLineStatus;
  /** False = record-only line; stock effects are skipped for its whole lifecycle. Null = legacy row. */
  stockReserved: boolean | null;
  createdAt: Date;
  updatedAt: Date;
}
